import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  Phone,
  Mail,
  Send,
  CheckCircle,
  Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email";
    if (!form.message.trim()) errs.message = "Message is required";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/contact`, form);
      if (res.data.success) {
        setSubmitted(true);
        toast.success("Message sent successfully!");
        setForm({ name: "", email: "", message: "" });
      }
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Header */}
      <section className="pt-[35px] pb-[35px] md:py-20 hero-gradient noise-bg">
        <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold bg-[#EAF2FF] text-[#1677FF] mb-4">
              Get in Touch
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              Contact{" "}
              <span className="text-[#1677FF]">Us</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Have a question or want to book a free trial class? We'd love to hear from you!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form + Details */}
      <section className="pt-[35px] pb-[35px] md:py-24">
        <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6">
          <div className="grid lg:grid-cols-[2fr_1fr] gap-10 lg:gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="p-6 md:p-8 rounded-2xl shadow-[var(--shadow-soft)] border border-border">
                {submitted ? (
                  <div className="text-center py-12" data-testid="contact-form-success">
                    <div className="w-16 h-16 rounded-full bg-[#E7FFF6] flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-[#00A676]" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">Thank You!</h3>
                    <p className="text-muted-foreground mb-6">
                      We've received your message and will get back to you within 24 hours.
                    </p>
                    <Button
                      onClick={() => setSubmitted(false)}
                      variant="outline"
                      className="rounded-full"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-1">Send us a message</h3>
                      <p className="text-sm text-muted-foreground">Fill out the form and we'll respond promptly.</p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="name" className="text-sm font-semibold mb-1.5 block">Full Name</Label>
                        <Input
                          id="name"
                          data-testid="contact-form-name-input"
                          placeholder="Your name"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          className={`rounded-xl h-11 ${errors.name ? "border-red-500" : ""}`}
                        />
                        {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-sm font-semibold mb-1.5 block">Email Address</Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="contact-form-email-input"
                          placeholder="your@email.com"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className={`rounded-xl h-11 ${errors.email ? "border-red-500" : ""}`}
                        />
                        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-sm font-semibold mb-1.5 block">Message</Label>
                        <Textarea
                          id="message"
                          data-testid="contact-form-message-textarea"
                          placeholder="How can we help? Tell us about your child and what programs interest you..."
                          rows={5}
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                          className={`rounded-xl resize-none ${errors.message ? "border-red-500" : ""}`}
                        />
                        {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message}</p>}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      data-testid="contact-form-submit-button"
                      disabled={loading}
                      className="rounded-full bg-[#1677FF] hover:bg-[#0F63DA] text-white shadow-[var(--btn-shadow)] btn-lift px-8 h-12 w-full sm:w-auto text-base font-semibold"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </Card>
            </motion.div>

            {/* Contact Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <Card className="p-6 rounded-2xl border border-border">
                <h3 className="font-bold text-base mb-4">Contact Information</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#E7FFF6] flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#00A676]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Email</p>
                      <a href="mailto:info@maplecodeacademy.ca" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                        info@maplecodeacademy.ca
                      </a>
                    </div>
                  </div>

                </div>
              </Card>

              <Card className="p-6 rounded-2xl border border-border bg-[#FBFBF7]">
                <h3 className="font-bold text-base mb-3">Why Choose Us?</h3>
                <ul className="space-y-2">
                  {[
                    "Free trial class available",
                    "Small groups (max 8 students)",
                    "Expert, certified instructors",
                    "Learn from anywhere online",
                    "Full refund guarantee",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-[#00A676] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
